import './plugins';
import '@/assets/main.scss';
import '@/services/intercom/intercom-global.js';
import RootLayout from '@/ui/layouts/RootLayout.vue';
import { createPinia } from 'pinia';
import Vue from 'vue';
import {
  fetchUser,
  loggedIn,
  user,
  userFetched,
} from '@/ui/auth/auth';
import { checkFeatures } from '@/ui/helpers/featureHelper';
import { router } from '@/ui/routes';
import store from '@/_legacy/store';
import { pipe } from 'fp-ts/lib/pipeable';
import * as TE from 'fp-ts/lib/TaskEither';
import * as T from 'fp-ts/lib/Task';
import { IO } from 'fp-ts/lib/IO';
import { ErrorFetchingUserPage as Error } from '@/ui/pages/error/ErrorFetchingUserPage';
import { ApiError } from './services/api';
import { setUser, unsetUser } from './services/telemetry';
import { initialize as initializeSentry } from './services/sentry';
import { isProd, isStaging } from '@/ui/helpers/systemHelper';
import { trackRoutes } from '@/ui/routes/guards/trackRoutes';
import { onboardingRedirect } from '@/ui/routes/guards/onboardingRedirect';
import { eventBus } from '@/_legacy/eventBus';
import { toasted } from '@/ui/helpers';
import * as Intercom from '@/services/intercom/intercom';
import { Userflow } from '@/services/userflow/userflow';
import 'animate.css';
import { getI18N, initialLocale, setLocale, setDateTimeFormats, t } from '@/ui/i18n/i18n';
import Buefy from 'buefy';
import emitter from './services/emitter';

Vue.config.productionTip = false;

if (isProd || isStaging) {
  initializeSentry();
}

router.beforeEach(trackRoutes);
router.beforeEach(onboardingRedirect);

const appCreated = () => {
  if (process.env.VUE_APP_INTERCOM_ENABLED === 'true') {
    Intercom.boot();
  }

  if (Userflow.isUserflowEnabled()) {
    Userflow.boot();
  }

  emitter.on('aiCreditsExhausted', () => {
    toasted.error(t('ai.credits-exhausted'));
  });

  // legacy
  eventBus.$on('error', (message: string) => {
    toasted.show(message, { type: 'error' });
  });
};

const mountApp: IO<void> = () => {
  // can be an empty user here if we open component which is for guest
  if (loggedIn.value && userFetched.value) {
    setUser(user.value);
    setLocale(user.value.locale);
    setDateTimeFormats(user.value.timezone);
  }

  const pinia = createPinia();

  // i18n is needed for legacy SFC components to use $t/$n/$d functions
  (new Vue({
    router,
    store,
    pinia,
    i18n: getI18N(),
    created: appCreated,
    render: h => h(RootLayout),
  }))
    .$mount('#app');
};

const mountError = (error: ApiError): IO<void> => () => {
  (new Vue({
    i18n: getI18N(),
    created: appCreated,
    render: h => h(Error, { props: { error } }),
  }))
    .$mount('#app');
};

const mountAppAfterFetchingUser = pipe(
  pipe(
    TE.Do,
    TE.bind('fetchedUser', () => fetchUser()),
    TE.chain(({ fetchedUser }) => {
      return TE.right(fetchedUser);
    }),
    TE.fold(
      error => T.task.fromIO(mountError(error)),
      () => T.task.fromIO(mountApp),
    ),
  ),
);

unsetUser();

setLocale(initialLocale());
checkFeatures()();

if (loggedIn.value) {
  mountAppAfterFetchingUser();
} else {
  mountApp();
}

Vue.use(Buefy);
